<script setup lang="ts">
    import { computed, ref, onMounted, watch } from 'vue'
    import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
    import { SavingState } from '@common/Common'
    import EditableValueSeriesSheet from '@common/Components/EditableValueSeriesSheet.vue'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import SaveButton from '@common/Components/SaveButton.vue'
    import Navigation from '@common/Navigation.vue'
    import ValueSeriesTable from '@Quest/_Common/Components/ValueSeriesTable.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectContextHeader from '@Quest/ProjectPlans/Components/ProjectContextHeader.vue'
    import { useProjectRevenuesQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useUpdateProjectRevenuesCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'
    import type { RevenuesPageReadModel } from '@Quest/ProjectPlans/readmodels'
    import { CurrencyDenomination, TimeFrameGranularity } from '@common/types'

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))

    const projectPlanId = computed<string | null>(() => rm.value?.ProjectPlanId ?? null)
    const granularity = computed<TimeFrameGranularity | null>(() => rm.value?.Granularity ?? null)
    const denomination = computed<CurrencyDenomination | null>(() => rm.value?.Denomination ?? null)

    const projectName = computed(() => rm.value?.ProjectName ?? '')
    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')
    const activePlanId = computed<string | null>(() => rm.value?.ActiveProjectPlanId ?? null)

    const userId = computed(() => rm.value?.UserId ?? 0)
    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)
    const title = computed(() => `${rm.value?.Title} (${rm.value?.CurrencyLabel})`)

    const hasPhases = computed(() => rm.value?.HasPhases ?? false)

    const inputSeries = computed(() => rm.value?.Series.filter(a => !a.IsTotal) ?? [])
    const isSubmitted = computed(() => rm.value?.IsSubmitted ?? false)
    const comment = computed(() => rm.value?.Comment ?? null)

    const sheet = ref<any>(null)
    const isEditing = ref(false)
    const savingState = ref<SavingState>(SavingState.Saved)
    const stopEditingLabel = computed(() => savingState.value === SavingState.Pending ? 'Cancel editing' : 'Back')
    const editingComment = ref<string | null>(null)

    const {
        runProjectRevenuesQueryV1,
        modelProjectRevenuesQueryV1
    } = useProjectRevenuesQueryV1(workspaceId, tchProjectId, projectId, projectPlanId, granularity, denomination)

    const rm = ref<RevenuesPageReadModel | null>(null)

    const {
        invokeUpdateProjectRevenuesCommandV1,
        onSucceedUpdateProjectRevenuesCommandV1
    } = useUpdateProjectRevenuesCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId, granularity, denomination)


    function startEditing()
    {
        if (!rm.value) return
        editingComment.value = rm.value.Comment
        isEditing.value = true
    }

    function cancelEditing()
    {
        if (savingState.value === SavingState.Saved)
        {
            isEditing.value = false
        }
        else if (
            savingState.value === SavingState.Pending &&
            confirm('You have unsaved changes. Are you sure you want to cancel editing?')
        )
        {
            savingState.value = SavingState.Saved
            isEditing.value = false
        }
    }

    function onDataChange()
    {
        savingState.value = SavingState.Pending
    }

    function handleSaveCommand()
    {
        if (!rm.value) return

        const entries = sheet.value?.getEntriesForSave()

        invokeUpdateProjectRevenuesCommandV1({
            Entries: entries,
            Comment: editingComment.value
        })

        savingState.value = SavingState.InProgress
    }

    function setDenomination(newDenomination: CurrencyDenomination)
    {
        const query = { ...route.query }

        query.denomination = newDenomination

        router.replace({
            params: { ...route.params },
            query
        })

        runProjectRevenuesQueryV1(
            {
                Denomination: newDenomination,
            })
    }

    function setGranularity(newGranularity: TimeFrameGranularity | null)
    {
        const query = { ...route.query }

        if (newGranularity)
            query.granularity = newGranularity
        else
            delete query.granularity

        router.replace({
            params: { ...route.params },
            query
        })

        runProjectRevenuesQueryV1(
            {
                Granularity: newGranularity,
            })
    }


    onMounted(() =>
    {
        document.title = 'Revenues | TransparentChoice'

        runProjectRevenuesQueryV1({
            ProjectPlanId: route.params.planId ? String(route.params.planId) : null,
            Granularity: route.query.granularity as TimeFrameGranularity ?? null,
            Denomination: route.query.denomination as CurrencyDenomination ?? null,
        });
    })

    watch(() => route.params.planId, () =>
    {
        runProjectRevenuesQueryV1({
            ProjectPlanId: route.params.planId as string | null
        })
    })

    watch(modelProjectRevenuesQueryV1, (model) =>
    {
        if (model?.RedirectUrl)
        {
            location.href = model.RedirectUrl
        }
        else
        {
            rm.value = model
        }
    })


    onSucceedUpdateProjectRevenuesCommandV1(() =>
    {
        runProjectRevenuesQueryV1()

        if (savingState.value === SavingState.InProgress)
        {
            savingState.value = SavingState.Saved
        }
    })

    onBeforeRouteLeave((_to, _from, next) =>
    {
        if (savingState.value === SavingState.Pending)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSaveCommand()
                next()
            }
            else
            {
                next()
            }
        }
        else
        {
            next()
        }
    })
</script>

<template>
    <div>
        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <ProjectContextHeader 
                            :projectName="projectName"
                            :planId="projectPlanId"
                            :planName="projectPlanName"
                            :activePlanId="activePlanId"/>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <SaveButton class="mr-1" v-if="isEditing" :savingState="savingState" @saveChanges="handleSaveCommand()"></SaveButton>
                    <button v-if="isEditing" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="cancelEditing">{{stopEditingLabel}}</button>

                    <!-- When NOT submitted -->
                    <button class="btn btn-success mr-1"
                            style="padding: 0.7em 2em;"
                            v-if="!isEditing && !isSubmitted"
                            @click="startEditing">
                        Edit revenues
                    </button>

                    <!-- When submitted -->
                    <button class="btn btn-outline-secondary mr-1"
                            style="padding: 0.7em 2em; cursor: default;"
                            v-if="!isEditing && isSubmitted"
                            disabled
                            title="You cannot edit a submitted plan">
                        <i class="fas fa-lock mr-2"></i> Submitted
                    </button>
                </div>
            </div>
            <hr />

            <ProjectNavigation ActivePage="revenues" class="mt-4"
                               :workspaceId="workspaceId"
                               :tchProjectId="tchProjectId"
                               :projectId="projectId"
                               :planId="projectPlanId" />

            <div v-if="isEditing">
                <div class="form-group mb-4">
                    <label for="revenues-comment" class="mb-1 text-muted" style="font-weight: 400; font-size: 0.97em;">
                        Assumptions or notes (optional)
                    </label>
                    <textarea id="revenues-comment"
                              v-model.trim="editingComment"
                               @input="onDataChange"
                              rows="3"
                              class="form-control">
                    </textarea>
                </div>

                <div class="alert alert-warning mb-1">
                    <strong>Note:</strong> Formula entered below will save as values.
                </div>

                <EditableValueSeriesSheet ref="sheet" :propTitle="title" :propSeries="inputSeries" :propColumns="rm.Columns" @dataChange="onDataChange" />
            </div>
            <div v-else>
                <div class="toolbar">
                    <div>
                        <div class="btn-group mr-2" role="group">
                            <span class="btn btn-secondary disabled">
                                Revenues
                            </span>

                            <router-link :to="{name: 'q-project-margins-path', params: { workspaceId, tchProjectId, projectId, planId: projectPlanId }, query: route.query }"
                                         class="btn btn-outline-secondary">
                                Margins
                            </router-link>
                        </div>

                        <div class="dropdown d-inline mr-2">
                            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                {{denomination}}s
                            </button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="setDenomination('Million')">Millions</a>
                                <a class="dropdown-item" @click="setDenomination('Thousand')">Thousands</a>
                                <a class="dropdown-item" @click="setDenomination('Unit')">Units</a>
                            </div>
                        </div>
                        <!--<div class="dropdown d-inline mr-1">
                            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                {{granularity ?? 'By Phase'}}
                            </button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="setGranularity('Monthly')">Monthly</a>
                                <a class="dropdown-item" @click="setGranularity('Quarterly')">Quarterly</a>
                                <a class="dropdown-item" @click="setGranularity('Yearly')">Yearly</a>
                                <a v-if="hasPhases" class="dropdown-item" @click="setGranularity()">By Phase</a>
                            </div>
                        </div>-->
                    </div>
                    <div></div>
                </div>

                <div v-if="comment" class="alert alert-info mt-3 mb-3" style="white-space: pre-line;">
                    <strong>Assumptions / Notes:</strong>
                    {{ comment }}
                </div>

                <ValueSeriesTable :propTitle="title" :propSeries="rm.Series" :propColumns="rm.Columns" :propStartDate="rm.ProjectStartDate" />
            </div>

        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
